import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  query,
  orderBy,
  Timestamp,
  CollectionReference,
  // DocumentReference,
  Query,
  where,
} from "firebase/firestore";
import { db } from "./firebaseConfig"; // Assume Firebase is configured elsewhere
import { Message } from "./message"; // Assume Message interface exists

/**
 * Interface representing a chat conversation between a property owner and guest user.
 *
 * This interface handles conversation metadata, message history, and Firestore integration
 * for a property rental application's chat system.
 */
export interface Conversation {
  /** Unique identifier for the conversation (typically constructed from participant IDs) */
  id: string;

  /** Array of participant IDs including ownerId, userId, and propertyId */
  participants: string[];

  /** Display title of the property being discussed in this conversation */
  propertyTitle: string;

  /** Optional URL pointing to the property's main image for display purposes */
  propertyImage?: string;

  /** Unique identifier of the property that this conversation is about */
  propertyId: string;

  /** Full display name of the property owner participating in the conversation */
  ownerFullName: string;

  /** Unique identifier of the property owner */
  ownerId: string;

  /** Full display name of the guest user participating in the conversation */
  userFullName: string;

  /** Unique identifier of the guest user */
  userId: string;

  /** Timestamp of when the most recent message was sent (used for sorting conversations) */
  lastMessageTime?: Date;

  /** Text content of the most recent message (for conversation preview) */
  lastMessage?: string;

  /** ID of the user who sent the most recent message */
  lastMessageSenderId?: string;

  /** Timestamp of when this conversation was initially created */
  createdAt?: Date;
}

/**
 * Utility class for managing conversation operations with Firestore.
 *
 * Provides static methods for CRUD operations, message handling, and data conversion
 * between JavaScript Date objects and Firestore Timestamps.
 */
export class ConversationService {
  /** Name of the Firestore collection where conversations are stored */
  private static readonly COLLECTION_NAME = "conversations";
  private static readonly MESSAGES_COLLECTION_NAME = "messages";

  /**
   * Converts a Firestore Timestamp to a JavaScript Date object.
   *
   * @param timestamp - Firestore Timestamp or null
   * @returns JavaScript Date object or undefined if input is null/undefined
   */
  private static timestampToDate(
    timestamp?: Timestamp | null
  ): Date | undefined {
    return timestamp?.toDate();
  }

  /**
   * Converts a JavaScript Date object to a Firestore Timestamp.
   *
   * @param date - JavaScript Date object or null/undefined
   * @returns Firestore Timestamp or null if input is null/undefined
   */
  private static dateToTimestamp(date?: Date | null): Timestamp | null {
    return date ? Timestamp.fromDate(date) : null;
  }

  /**
   * Gets a reference to the conversations collection in Firestore.
   *
   * @returns CollectionReference pointing to the 'conversations' collection
   */
  private static getCollection(): CollectionReference {
    return collection(db, this.COLLECTION_NAME);
  }

  /**
   * Converts raw Firestore document data to a Conversation object.
   *
   * Handles the conversion of Firestore Timestamps to JavaScript Date objects
   * and ensures all required fields are properly typed.
   *
   * @param data - Raw document data from Firestore
   * @param id - Document ID to assign to the conversation
   * @returns Properly typed Conversation object
   */
  private static fromFirestore(data: any, id: string): Conversation {
    return {
      id,
      participants: data.participants || [],
      propertyTitle: data.propertyTitle || "",
      propertyImage: data.propertyImage,
      propertyId: data.propertyId || "",
      ownerFullName: data.ownerFullName || "",
      ownerId: data.ownerId || "",
      userFullName: data.userFullName || "",
      userId: data.userId || "",
      lastMessageTime: this.timestampToDate(data.lastMessageTime),
      lastMessage: data.lastMessage,
      lastMessageSenderId: data.lastMessageSenderId,
      createdAt: this.timestampToDate(data.createdAt),
    };
  }

  /**
   * Converts a Conversation object to Firestore-compatible data.
   *
   * Handles the conversion of JavaScript Date objects to Firestore Timestamps
   * and removes the ID field (since it's stored as the document key).
   *
   * @param conversation - Conversation object to convert
   * @returns Plain object suitable for Firestore operations
   */
  private static toFirestore(conversation: Conversation): Record<string, any> {
    return {
      ...conversation,
      lastMessageTime: this.dateToTimestamp(conversation.lastMessageTime),
      createdAt: this.dateToTimestamp(conversation.createdAt),
    };
  }

  /**
   * Creates a new conversation or retrieves an existing one.
   *
   * This method implements an "upsert" pattern - it first checks if a conversation
   * already exists between the specified participants for the given property.
   * If found, returns the existing conversation. If not, creates a new one.
   *
   * The conversation ID is deterministically generated from participant IDs
   * to ensure consistency across multiple calls with the same parameters.
   *
   * @param params - Object containing all required conversation data
   * @returns Promise resolving to either existing or newly created conversation
   */
  static async createOrGet(params: {
    propertyId: string;
    propertyTitle: string;
    propertyImage?: string;
    ownerId: string;
    userId: string;
    ownerFullName: string;
    userFullName: string;
  }): Promise<Conversation> {
    // Create consistent participant list for ID generation
    const participants = [params.ownerId, params.userId, params.propertyId];

    // Generate deterministic document ID from participant IDs
    const docId = participants.join("_");

    // Get reference to the specific conversation document
    const docRef = doc(this.getCollection(), docId);

    // Check if conversation already exists
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      // Return existing conversation with proper type conversion
      return this.fromFirestore(docSnapshot.data(), docSnapshot.id);
    }

    // Create new conversation object with current timestamp
    const newConversation: Conversation = {
      id: docId,
      participants,
      propertyId: params.propertyId,
      propertyTitle: params.propertyTitle,
      propertyImage: params.propertyImage,
      ownerId: params.ownerId,
      ownerFullName: params.ownerFullName,
      userId: params.userId,
      userFullName: params.userFullName,
      createdAt: new Date(),
    };

    // Save new conversation to Firestore
    await setDoc(docRef, this.toFirestore(newConversation));

    return newConversation;
  }

  /**
   * Updates the metadata for the most recent message in a conversation.
   *
   * This method is typically called after a new message is sent to maintain
   * conversation-level metadata that's used for displaying conversation lists
   * and sorting by recent activity.
   *
   * @param conversationId - ID of the conversation to update
   * @param params - Object containing message metadata
   */
  static async updateLastMessage(
    conversationId: string,
    params: {
      message: string;
      senderId: string;
      sentAt: Date;
    }
  ): Promise<void> {
    const docRef = doc(this.getCollection(), conversationId);

    // Update only the last message fields
    await updateDoc(docRef, {
      lastMessage: params.message,
      lastMessageTime: this.dateToTimestamp(params.sentAt),
      lastMessageSenderId: params.senderId,
    });
  }

  /**
   * Gets a reference to the messages subcollection for a specific conversation.
   *
   * Firestore uses a hierarchical structure where messages are stored as
   * subcollections under each conversation document. This provides better
   * scalability and query performance for chat applications.
   *
   * @param conversationId - ID of the parent conversation
   * @returns CollectionReference pointing to the messages subcollection
   */
  private static getMessagesCollection(
    conversationId: string
  ): CollectionReference {
    return collection(
      db,
      this.COLLECTION_NAME,
      conversationId,
      this.MESSAGES_COLLECTION_NAME
    );
  }

  /**
   * Sends a new message in a conversation.
   *
   * This method performs two operations atomically:
   * 1. Adds the new message to the messages subcollection
   * 2. Updates the conversation's last message metadata
   *
   * In a production environment, this should ideally be wrapped in a
   * Firestore transaction to ensure consistency.
   *
   * @param conversationId - ID of the conversation to send message in
   * @param params - Object containing message details
   * @returns Promise that resolves when both operations complete
   */
  static async sendMessage(
    conversationId: string,
    params: {
      senderId: string;
      senderName: string;
      body: string;
    }
  ): Promise<void> {
    const now = new Date();

    // Create new message object
    const newMessage: Message = {
      id: "", // Will be auto-generated by Firestore
      senderId: params.senderId,
      senderName: params.senderName,
      body: params.body,
      sentAt: now,
      isRead: false,
    };

    // Add message to subcollection
    await addDoc(this.getMessagesCollection(conversationId), {
      senderId: newMessage.senderId,
      senderName: newMessage.senderName,
      body: newMessage.body,
      sentAt: this.dateToTimestamp(newMessage.sentAt),
    });

    // Update conversation metadata with last message info
    await this.updateLastMessage(conversationId, {
      message: params.body,
      senderId: params.senderId,
      sentAt: now,
    });
  }

  /**
   * Creates a query to retrieve messages for a conversation.
   *
   * Returns messages ordered by timestamp in descending order (newest first),
   * which is the typical display order for chat applications.
   *
   * The returned Query can be used with Firestore's real-time listeners
   * for live chat functionality, or with get() for one-time retrieval.
   *
   * @param conversationId - ID of the conversation to query messages for
   * @returns Query object that can be executed to retrieve ordered messages
   */
  static getMessagesQuery(conversationId: string): Query {
    return query(
      this.getMessagesCollection(conversationId),
      orderBy("sentAt", "desc") // Newest messages first
    );
  }

  /**
   * Creates a query to retrieve conversations.
   *
   * Returns conversations ordered by timestamp in descending order (newest first),
   * which is the typical display order for chat applications.
   *
   * When userId is provided, it filters conversations to only include those where
   * the user is a participant (either as owner or guest).
   *
   * The returned Query can be used with Firestore's real-time listeners
   * for live chat functionality, or with get() for one-time retrieval.
   *
   * @param userId - ID of the user to filter conversations for
   * @returns Query object that can be executed to retrieve ordered conversations
   */
  static getConversationQuery(userId: string): Query {
    // Filter conversations where the user is a participant
    return query(
      this.getCollection(),
      where("userId", "==", userId),
      orderBy("lastMessageTime", "desc") // Newest conversations first
    );
  }

  /**
   * Retrieves a single conversation by its ID.
   *
   * @param conversationId - ID of the conversation to retrieve
   * @returns Promise resolving to the conversation or null if not found
   */
  static async getById(conversationId: string): Promise<Conversation | null> {
    const docRef = doc(this.getCollection(), conversationId);
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      return this.fromFirestore(docSnapshot.data(), docSnapshot.id);
    }

    return null;
  }
}

// Export the interface for use in other parts of the application
export default ConversationService;
